import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import {
  BlockCollapseToggle,
  BlockHeader,
  FIELD_HINT_SX,
  FORM_GROUP_TITLE_SX,
  FORM_PRIMARY_BUTTON_SX,
  FORM_SECONDARY_BUTTON_SX,
  LotUserPrivateInfoSection,
  ORGANIZE_BLOCK_SX,
  PRIVATE_BLOCK_SX,
  PUBLICATION_BLOCK_SX,
  SECTION_BAR_ICON_BUTTON_SX,
  SECTION_BAR_ICON_BUTTON_DELETE_SX,
} from './formBlockUi';
import { toast } from '../../../../Components/Toast';
import { confirmDialog } from '../../../../Components/ConfirmDialog';
import {
  getLotUserContent,
  upsertLotUserContent,
  deleteLotUserContent,
} from '../../../../services/lotUserContentService';
import {
  LOT_CONTENT_MAX_TITLE,
  LOT_CONTENT_MAX_DESC,
  LOT_CONTENT_MAX_OBS,
  LOT_CONTENT_CURRENCIES,
  LOT_CONTENT_OPERATIONS,
  LOT_CONTENT_OPERATION_LABEL,
  emptyLotUserContentForm,
  mapLotUserContentFromApi,
  buildLotUserContentUpsertPayload,
  hasSharedLotUserContentDisplayData,
} from '../../../utils/lotUserContentFormUtils';
import {
  LISTING_MIN_IMAGES,
  LISTING_UPSERT_MIN_PHOTOS_TOAST,
  normalizeListingImagesFromPayload,
  isListingImageMinError,
  resolveImageApiMessage,
} from '../../../utils/listingImageUtils';
import { normalizeListingVideosFromPayload } from '../../../utils/listingVideoUtils';
import {
  formatPriceInputDisplay,
  parsePriceInputValue,
} from '../../../utils/listingCoreConstants';
import { colors } from '../../../../styles/theme';
import LotContentImages from './LotContentImages';
import LotContentVideos from './LotContentVideos';
import LotContentAttachments from './LotContentAttachments';
import { normalizeListingAttachmentsFromPayload } from '../../../utils/listingAttachmentUtils';
import {
  PRIVATE_BLOCK_TITLE_DEFAULT,
  resolveSpaceGuestWriteError,
} from '../../../utils/spaceMirrorLots';
import UserLotListMultiSelect from './UserLotListMultiSelect';

const LotUserContentForm = ({
  lotId,
  fieldSx = {},
  secondaryTextSx = { color: 'rgba(255,255,255,0.45)', fontSize: '12px' },
  userLotLists = [],
  onListCreated,
  onCancel,
  onSaved,
  listingCoreOnly = false,
  initialContent = null,
  privateBlockTitle = PRIVATE_BLOCK_TITLE_DEFAULT,
  // Espacio ajeno (F2): edita el contenido del owner (`owner_user_id`). La API
  // aplica reglas de editor: solo copy comercial; etiqueta, notas y
  // listas no se guardan, así que no se muestran. Media: solo sumar.
  spaceOwnerUserId = null,
}) => {
  const spaceGuestEdit = Boolean(spaceOwnerUserId) && !listingCoreOnly;
  const writeOwnerUserId = spaceGuestEdit ? spaceOwnerUserId : null;
  const coreFieldsOnly = listingCoreOnly || spaceGuestEdit;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState(emptyLotUserContentForm);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loadedHasContent, setLoadedHasContent] = useState(false);
  const [privateExpanded, setPrivateExpanded] = useState(false);
  const [organizeExpanded, setOrganizeExpanded] = useState(false);

  const inputLabelSx = {
    color: 'rgba(255,255,255,0.72)',
    '&.Mui-focused': { color: '#ffffff' },
  };
  const lotFieldSx = {
    ...fieldSx,
    '& .MuiOutlinedInput-root': {
      ...(fieldSx?.['& .MuiOutlinedInput-root'] || {}),
      backgroundColor: '#242424',
      borderRadius: '8px',
      '& fieldset': { borderColor: '#4A4A4A' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
      '&.Mui-focused fieldset': { borderColor: colors.primary },
    },
  };
  const selectSx = {
    backgroundColor: '#242424',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.65)' },
    '& .MuiSelect-select': { color: '#ffffff' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4A4A4A' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
  };
  const menuItemSx = {
    color: '#ffffff',
    fontSize: '13px',
    height: '34px',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
    '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.10)' },
    '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
  };
  const listSelectMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 360,
        bgcolor: '#1d1d1d',
        border: '1px solid rgba(255,255,255,0.10)',
        backgroundImage: 'none',
      },
    },
  };

  useEffect(() => {
    if (!lotId) return undefined;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await getLotUserContent(lotId, { ownerUserId: writeOwnerUserId });
        if (cancelled) return;
        if (!res._httpOk) {
          if (initialContent) {
            setForm(mapLotUserContentFromApi(initialContent));
            setLoadedHasContent(hasSharedLotUserContentDisplayData(initialContent));
            return;
          }
          toast.error({ title: 'Error', message: 'No se pudo cargar la información del lote.' });
          return;
        }
        const data = res.data || {};
        const mapped = mapLotUserContentFromApi(data);
        setForm(mapped);
        setImages(normalizeListingImagesFromPayload(data));
        setVideos(normalizeListingVideosFromPayload(data));
        setAttachments(normalizeListingAttachmentsFromPayload(data));
        setLoadedHasContent(Boolean(data.counts?.has_content));
        const hasPrivateData = Boolean(mapped.unit_label?.trim() || mapped.observations?.trim());
        setPrivateExpanded(hasPrivateData);
        setOrganizeExpanded(Array.isArray(mapped.list_ids) && mapped.list_ids.length > 0);
      } catch (e) {
        if (!cancelled) {
          console.error('Error loading lot content:', e);
          toast.error({ title: 'Error', message: 'No se pudo cargar la información del lote.' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  // initialContent solo se usa si el GET falla (editor de colección).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotId, writeOwnerUserId]);

  const canDelete = !coreFieldsOnly && (loadedHasContent || images.length > 0 || videos.length > 0 || attachments.length > 0);
  const isBusy = saving || deleting;

  const handleDelete = async () => {
    if (!lotId || !canDelete) return;

    const result = await confirmDialog({
      title: '¿Eliminar información privada?',
      description: 'Se borrarán todos los datos privados, las fotos, los videos y los adjuntos de este lote. Esta acción no se puede deshacer.',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!result.isConfirmed) return;

    setDeleting(true);
    try {
      const res = await deleteLotUserContent(lotId);
      if (!res._httpOk) {
        toast.error({ title: 'Error', message: 'No se pudo eliminar la información.' });
        return;
      }

      const refreshRes = await getLotUserContent(lotId);
      const data = refreshRes._httpOk ? (refreshRes.data || {}) : { lot_id: lotId, images: [], videos: [], attachments: [], counts: { has_content: false } };

      toast.success('Información eliminada correctamente.');
      onSaved?.(data);
      onCancel?.();
    } catch (e) {
      console.error('Error deleting lot content:', e);
      toast.error({ title: 'Error', message: 'No se pudo eliminar la información.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!lotId) return;
    if (!listingCoreOnly && images.length < LISTING_MIN_IMAGES) {
      toast.warning(LISTING_UPSERT_MIN_PHOTOS_TOAST);
      return;
    }
    setSaving(true);
    try {
      const res = await upsertLotUserContent(
        buildLotUserContentUpsertPayload(lotId, form, { listingCoreOnly: coreFieldsOnly }),
        { ownerUserId: writeOwnerUserId },
      );
      if (!res._httpOk) {
        const msg = resolveImageApiMessage(res);
        const spaceError = resolveSpaceGuestWriteError(res._httpStatus, writeOwnerUserId, {
          message: res.error || res.message,
        });
        if (spaceError) {
          toast.error({ title: 'Error', message: spaceError });
        } else if (isListingImageMinError(res._httpStatus, msg)) {
          toast.warning(LISTING_UPSERT_MIN_PHOTOS_TOAST);
        } else {
          toast.error({ title: 'Error', message: 'No se pudo guardar la información.' });
        }
        return;
      }

      // Cuidado post-save (mismo patrón UF): upsert/get a veces omiten media.
      // Si llegara []/undefined, no pisar uploads locales de fotos/videos/adjuntos.
      const refreshRes = await getLotUserContent(lotId, { ownerUserId: writeOwnerUserId });
      const upsertData = res.data && typeof res.data === 'object' ? res.data : {};
      const refreshData = refreshRes._httpOk && refreshRes.data && typeof refreshRes.data === 'object'
        ? refreshRes.data
        : null;
      const pickMedia = (key, local) => {
        const remote = refreshData?.[key] ?? upsertData[key];
        if (Array.isArray(remote) && remote.length > 0) return remote;
        if (Array.isArray(local) && local.length > 0) return local;
        if (Array.isArray(remote)) return remote;
        return Array.isArray(local) ? local : [];
      };
      const data = {
        ...(refreshData || upsertData || { lot_id: lotId }),
        images: pickMedia('images', images),
        videos: pickMedia('videos', videos),
        attachments: pickMedia('attachments', attachments),
      };

      toast.success('Información guardada correctamente.');
      onSaved?.(data);
      onCancel?.();
    } catch (e) {
      console.error('Error saving lot content:', e);
      toast.error({ title: 'Error', message: 'No se pudo guardar la información.' });
    } finally {
      setSaving(false);
    }
  };

  const privateInfoActions = (
    <>
      {canDelete && (
        <Tooltip title="Eliminar" placement="top" arrow>
          <span>
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={isBusy}
              aria-label="Eliminar información privada"
              sx={SECTION_BAR_ICON_BUTTON_DELETE_SX}
            >
              {deleting
                ? <CircularProgress size={12} sx={{ color: '#f87171' }} />
                : <DeleteOutlineIcon />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Volver" placement="top" arrow>
        <span>
          <IconButton
            size="small"
            onClick={onCancel}
            disabled={isBusy}
            aria-label="Volver al panel del lote"
            sx={SECTION_BAR_ICON_BUTTON_SX}
          >
            <ArrowBackIcon />
          </IconButton>
        </span>
      </Tooltip>
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, minHeight: 0 }}>
      <LotUserPrivateInfoSection loading={loading} actions={privateInfoActions}>
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, flex: 1, overflowY: 'auto' }}>

            {/* Bloque 1 — privado (Solo en tu espacio; Solo vos en shares). Invitado: la API no lo guarda */}
            {!coreFieldsOnly ? (
            <Box sx={{ ...PRIVATE_BLOCK_SX, p: 1.5 }}>
              <BlockHeader
                icon={<LockOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)', mt: 0.15 }} />}
                title={privateBlockTitle}
                subtitle="Campos privados que no se comparten con visitantes."
                action={(
                  <BlockCollapseToggle
                    expanded={privateExpanded}
                    onToggle={() => setPrivateExpanded((v) => !v)}
                    labelExpanded="Contraer sección privada"
                    labelCollapsed="Expandir sección privada"
                  />
                )}
              />
              <Collapse in={privateExpanded}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.25 }}>
                  <Box>
                    <TextField
                      label="Etiqueta interna"
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder='Ej: Depto 2B, Casa del fondo, Local 3…'
                      value={form.unit_label}
                      inputProps={{ maxLength: 80 }}
                      onChange={(e) => setForm((p) => ({ ...p, unit_label: e.target.value }))}
                      sx={lotFieldSx}
                    />
                    <Typography component="span" sx={FIELD_HINT_SX}>
                      Para distinguir unidades en este lote (ej. &quot;Depto 1A&quot;).
                    </Typography>
                  </Box>

                  <Box>
                    <TextField
                      label="Notas privadas"
                      fullWidth
                      size="small"
                      variant="outlined"
                      multiline
                      rows={2}
                      value={form.observations}
                      inputProps={{ maxLength: LOT_CONTENT_MAX_OBS }}
                      onChange={(e) => setForm((p) => ({ ...p, observations: e.target.value }))}
                      sx={lotFieldSx}
                    />
                    <Typography variant="caption" sx={{
                      color: (form.observations || '').length > LOT_CONTENT_MAX_OBS * 0.9 ? '#f87171' : '#ABABAB',
                      fontSize: '12px',
                      float: 'right',
                    }}>
                      {(form.observations || '').length}/{LOT_CONTENT_MAX_OBS}
                    </Typography>
                  </Box>
                </Box>
              </Collapse>
            </Box>
            ) : null}

            {/* Bloque 2 — Publicación */}
            <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <BlockHeader
                icon={<PublicOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', mt: 0.15 }} />}
                title="Publicación"
                subtitle={spaceGuestEdit ? undefined : 'Contenido descriptivo del lote.'}
              />

              <Box>
                <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 1.25 }}>Presentación</Typography>
                <TextField
                  label={<>Título <Typography component="span" sx={{ color: '#f87171', ml: 0.3, fontSize: '12px' }}>*</Typography></>}
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={form.title}
                  inputProps={{ maxLength: LOT_CONTENT_MAX_TITLE }}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  sx={lotFieldSx}
                />
                <Typography variant="caption" sx={{
                  color: (form.title || '').length > LOT_CONTENT_MAX_TITLE * 0.9 ? '#f87171' : '#ABABAB',
                  fontSize: '12px',
                  float: 'right',
                }}>
                  {(form.title || '').length}/{LOT_CONTENT_MAX_TITLE}
                </Typography>
              </Box>

              <Box>
                <TextField
                  label="Descripción"
                  fullWidth
                  size="small"
                  variant="outlined"
                  multiline
                  rows={2}
                  value={form.description}
                  inputProps={{ maxLength: LOT_CONTENT_MAX_DESC }}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  sx={lotFieldSx}
                />
                <Typography variant="caption" sx={{
                  color: (form.description || '').length > LOT_CONTENT_MAX_DESC * 0.9 ? '#f87171' : '#ABABAB',
                  fontSize: '12px',
                  float: 'right',
                }}>
                  {(form.description || '').length}/{LOT_CONTENT_MAX_DESC}
                </Typography>
              </Box>

              <Box sx={{ pt: 0.5 }}>
                <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 1.25 }}>Datos comerciales</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    label="Monto"
                    size="small"
                    variant="outlined"
                    type="text"
                    inputMode="numeric"
                    value={formatPriceInputDisplay(form.price)}
                    onChange={(e) => setForm((p) => ({
                      ...p,
                      price: parsePriceInputValue(e.target.value),
                    }))}
                    InputProps={{ startAdornment: <Typography variant="caption" sx={secondaryTextSx}>$</Typography> }}
                    sx={{ ...lotFieldSx, flex: '1.5 1 140px', minWidth: 0 }}
                  />
                  <FormControl size="small" sx={{ flex: '1 1 100px', minWidth: 0 }}>
                    <InputLabel sx={inputLabelSx} shrink>
                      Moneda
                    </InputLabel>
                    <Select
                      value={form.currency || 'USD'}
                      label="Moneda"
                      variant="outlined"
                      notched
                      onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                      sx={selectSx}
                      MenuProps={listSelectMenuProps}
                    >
                      {LOT_CONTENT_CURRENCIES.map((c) => (
                        <MenuItem key={c.value} value={c.value} sx={menuItemSx}>{c.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: '1.2 1 140px', minWidth: 0 }}>
                    <InputLabel sx={inputLabelSx} shrink>
                      Operación
                    </InputLabel>
                    <Select
                      value={form.operation || ''}
                      label="Operación"
                      variant="outlined"
                      notched
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography
                              component="span"
                              sx={{ color: 'rgba(255,255,255,0.40)', fontSize: '14px' }}
                            >
                              Seleccionar
                            </Typography>
                          );
                        }
                        return LOT_CONTENT_OPERATION_LABEL[selected] || selected;
                      }}
                      onChange={(e) => setForm((p) => ({ ...p, operation: e.target.value }))}
                      sx={selectSx}
                      MenuProps={listSelectMenuProps}
                    >
                      <MenuItem value="" sx={menuItemSx}>
                        <em>Sin operación</em>
                      </MenuItem>
                      {LOT_CONTENT_OPERATIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={menuItemSx}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {!listingCoreOnly ? (
              <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 1.25 }}>
                <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 1.25 }}>Fotos</Typography>
                <LotContentImages
                  lotId={lotId}
                  canEdit
                  initialImages={images}
                  onImagesChange={setImages}
                  ownerUserId={writeOwnerUserId}
                  additiveOnly={spaceGuestEdit}
                />
              </Box>
              ) : null}

              {!listingCoreOnly ? (
              <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 1.25 }}>
                <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 1.25 }}>Videos</Typography>
                <LotContentVideos
                  lotId={lotId}
                  canEdit
                  initialVideos={videos}
                  onVideosChange={setVideos}
                  ownerUserId={writeOwnerUserId}
                  additiveOnly={spaceGuestEdit}
                />
              </Box>
              ) : null}

              {!listingCoreOnly ? (
              <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', pt: 1.25 }}>
                <Typography sx={{ ...FORM_GROUP_TITLE_SX, mb: 1.25 }}>Adjuntos</Typography>
                <LotContentAttachments
                  lotId={lotId}
                  canEdit
                  initialAttachments={attachments}
                  onAttachmentsChange={setAttachments}
                  ownerUserId={writeOwnerUserId}
                  additiveOnly={spaceGuestEdit}
                />
              </Box>
              ) : null}
            </Box>

            {/* Bloque 3 — Listas (invitado: la API no sincroniza list_ids) */}
            {!coreFieldsOnly ? (
            <Box sx={{ ...ORGANIZE_BLOCK_SX, p: 1.5 }}>
              <BlockHeader
                icon={<FolderOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)' }} />}
                title="Listas"
                action={(
                  <BlockCollapseToggle
                    expanded={organizeExpanded}
                    onToggle={() => setOrganizeExpanded((v) => !v)}
                    labelExpanded="Contraer listas"
                    labelCollapsed="Expandir listas"
                  />
                )}
              />
              <Collapse in={organizeExpanded}>
                <Box sx={{ mt: 1.25 }}>
                  <UserLotListMultiSelect
                    id="lot-content-list-select"
                    userLotLists={userLotLists}
                    value={Array.isArray(form.list_ids) ? form.list_ids : []}
                    onChange={(next) => setForm((p) => ({ ...p, list_ids: next }))}
                    onListCreated={onListCreated}
                  />
                </Box>
              </Collapse>
            </Box>
            ) : null}
          </Box>

          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            borderTop: '1px solid #383838',
            bgcolor: 'rgba(0,0,0,0.18)',
          }}>
            <Button
              onClick={onCancel}
              disabled={isBusy}
              sx={FORM_SECONDARY_BUTTON_SX}
            >
              Cancelar
            </Button>
            <Button
              startIcon={saving
                ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />
                : <SaveIcon sx={{ fontSize: '18px !important' }} />}
              onClick={handleSave}
              disabled={isBusy || !form.title?.trim()}
              sx={FORM_PRIMARY_BUTTON_SX}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Box>
        </>
      </LotUserPrivateInfoSection>
    </Box>
  );
};

export default LotUserContentForm;
